import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarClock, Radio, Users, Car, FileCheck, PhoneIncoming, Building2, PoundSterling, CreditCard, Tag, Star, MessageSquare, Ticket, FileText, Settings as SettingsIcon, Loader2, Search, Clock, UserCheck } from "lucide-react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { DashboardShell, StatCard, StatusBadge } from "@/components/dashboard/DashboardShell";
import { SupportChats, SupportTickets } from "@/pages/admin/Support";
import { CmsManager, SettingsManager } from "@/pages/admin/CmsSettings";
import { api } from "@/lib/api";
import { gbp } from "@/lib/constants";

const NAV = [
  { key: "overview", label: "Dashboard", icon: LayoutDashboard },
  { key: "bookings", label: "Bookings", icon: CalendarClock },
  { key: "live", label: "Live Bookings", icon: Radio },
  { key: "drivers", label: "Drivers", icon: Users },
  { key: "vehicles", label: "Vehicles", icon: Car },
  { key: "applications", label: "Driver Applications", icon: FileCheck },
  { key: "leads", label: "Taxi Requests", icon: PhoneIncoming },
  { key: "corporate", label: "Corporate", icon: Building2 },
  { key: "pricing", label: "Pricing", icon: PoundSterling },
  { key: "payments", label: "Payments", icon: CreditCard },
  { key: "promotions", label: "Promotions", icon: Tag },
  { key: "reviews", label: "Ratings & Reviews", icon: Star },
  { key: "support", label: "Support Chats", icon: MessageSquare },
  { key: "tickets", label: "Support Tickets", icon: Ticket },
  { key: "cms", label: "CMS", icon: FileText },
  { key: "settings", label: "Settings", icon: SettingsIcon },
];
const PIE = ["#1D4ED8", "#0F172A", "#D97706", "#10B981", "#0EA5E9", "#8B5CF6"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  useEffect(() => {
    api.post("/support/presence", { online: true }).catch(() => {});
    return () => { api.post("/support/presence", { online: false }).catch(() => {}); };
  }, []);
  return (
    <DashboardShell title="Admin" nav={NAV} active={active} onNav={setActive}>
      {active === "overview" && <Overview />}
      {active === "bookings" && <Bookings />}
      {active === "live" && <Live />}
      {active === "drivers" && <Drivers />}
      {active === "vehicles" && <Vehicles />}
      {active === "applications" && <Applications />}
      {active === "leads" && <Leads />}
      {active === "corporate" && <Corporate />}
      {active === "pricing" && <Pricing />}
      {active === "payments" && <Payments />}
      {active === "promotions" && <Promotions />}
      {active === "reviews" && <Reviews />}
      {active === "support" && <SupportChats />}
      {active === "tickets" && <SupportTickets />}
      {active === "cms" && <CmsManager />}
      {active === "settings" && <SettingsManager />}
    </DashboardShell>
  );
}

function Section({ children }) { return <div className="space-y-6">{children}</div>; }
function Loading() { return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>; }

function Overview() {
  const [d, setD] = useState(null);
  const [sup, setSup] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setD(data));
    api.get("/support/stats").then(({ data }) => setSup(data)).catch(() => {});
  }, []);
  if (!d) return <Loading />;
  const k = d.kpis;
  return (
    <Section>
      {sup && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
          <StatCard icon={CalendarClock} label="Today's Bookings" value={sup.todays_bookings} />
          <StatCard icon={Radio} label="Active Journeys" value={sup.active_journeys} color="amber" />
          <StatCard icon={Users} label="Available Drivers" value={sup.available_drivers} color="emerald" />
          <StatCard icon={MessageSquare} label="Open Chats" value={sup.open_chats} />
          <StatCard icon={Clock} label="Waiting Chats" value={sup.waiting_chats} color="amber" />
          <StatCard icon={UserCheck} label="Active Agents" value={sup.active_agents} color="emerald" />
          <StatCard icon={Star} label="Avg Chat Rating" value={sup.avg_chat_rating || "—"} color="amber" />
          <StatCard icon={Ticket} label="Unresolved Tickets" value={sup.unresolved_tickets} color="navy" />
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={CalendarClock} label="Total bookings" value={k.total_bookings} />
        <StatCard icon={Radio} label="Active journeys" value={k.active_journeys} color="amber" />
        <StatCard icon={PoundSterling} label="Revenue" value={gbp(k.revenue)} color="emerald" />
        <StatCard icon={CalendarClock} label="Today's bookings" value={k.todays_bookings} color="navy" />
        <StatCard icon={Star} label="Completed trips" value={k.completed_trips} color="emerald" />
        <StatCard icon={FileCheck} label="Driver applications" value={k.driver_applications} color="amber" />
        <StatCard icon={PhoneIncoming} label="Open taxi requests" value={k.open_leads} />
        <StatCard icon={Users} label="Online drivers" value={k.online_drivers} color="navy" />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <h3 className="mb-4 font-heading font-semibold text-navy">Bookings & revenue (last 10 days)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.by_day}><XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} /><YAxis fontSize={11} tickLine={false} axisLine={false} /><Tooltip /><Line type="monotone" dataKey="bookings" stroke="#1D4ED8" strokeWidth={2.5} dot={false} /><Line type="monotone" dataKey="revenue" stroke="#D97706" strokeWidth={2.5} dot={false} /></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-4 font-heading font-semibold text-navy">Bookings by service</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart><Pie data={d.by_service} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>{d.by_service.map((e, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 font-heading font-semibold text-navy">Top pickup locations</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={d.top_locations}><XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} /><YAxis fontSize={11} tickLine={false} axisLine={false} /><Tooltip /><Bar dataKey="value" fill="#1D4ED8" radius={[6, 6, 0, 0]} /></BarChart>
        </ResponsiveContainer>
      </div>
    </Section>
  );
}

function Bookings() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/bookings", { params: { status: status === "all" ? undefined : status, q: q || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status]);
  return (
    <Section>
      <div className="flex flex-wrap gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3"><Search size={15} className="text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search ref, name, phone, driver" className="border-0 focus-visible:ring-0" data-testid="admin-booking-search" /></div>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-44" data-testid="admin-status-filter"><SelectValue /></SelectTrigger><SelectContent>{["all", "pending", "confirmed", "driver_assigned", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
        <Button variant="outline" onClick={load}>Search</Button>
      </div>
      {!rows ? <Loading /> : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Ref</th><th className="p-3">Customer</th><th className="p-3">Journey</th><th className="p-3">Driver</th><th className="p-3">Total</th><th className="p-3">Status</th></tr></thead>
            <tbody>{rows.map((b) => (
              <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-booking-${b.id}`}>
                <td className="p-3 font-medium text-navy">{b.booking_ref}</td>
                <td className="p-3">{b.customer?.name}</td>
                <td className="p-3 text-slate-600">{b.pickup?.name}{b.dropoff ? ` → ${b.dropoff.name}` : ""}</td>
                <td className="p-3">{b.driver_name || "—"}</td>
                <td className="p-3 font-semibold">{gbp(b.total)}</td>
                <td className="p-3"><StatusBadge status={b.status} /></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function Live() {
  const [d, setD] = useState(null);
  useEffect(() => { const l = () => api.get("/admin/live").then(({ data }) => setD(data)); l(); const t = setInterval(l, 6000); return () => clearInterval(t); }, []);
  if (!d) return <Loading />;
  return (
    <Section>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={Radio} label="Active bookings" value={d.bookings.length} color="amber" />
        <StatCard icon={Users} label="Online drivers" value={d.drivers.length} color="emerald" />
        <StatCard icon={Car} label="On trip" value={d.bookings.filter((b) => ["picked_up", "journey_started"].includes(b.status)).length} />
      </div>
      <div className="grid gap-3">
        {d.bookings.length === 0 ? <p className="text-sm text-slate-500">No active journeys right now.</p> : d.bookings.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div><span className="font-semibold text-navy">{b.booking_ref}</span> <span className="text-sm text-slate-500">{b.pickup?.name} → {b.dropoff?.name || "—"}</span><p className="text-xs text-slate-400">{b.driver_name} · {b.operator_name}</p></div>
            <StatusBadge status={b.status} />
          </div>
        ))}
      </div>
    </Section>
  );
}

function Drivers() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/drivers").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => { await api.patch(`/admin/drivers/${id}/status`, { status }); toast.success("Driver updated"); load(); };
  if (!rows) return <Loading />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Driver</th><th className="p-3">Operator</th><th className="p-3">City</th><th className="p-3">Rating</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
        <tbody>{rows.map((d) => (
          <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="p-3 font-medium text-navy">{d.name}</td><td className="p-3">{d.operator_name}</td><td className="p-3">{d.city}</td><td className="p-3">{d.rating} ★</td>
            <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${d.status === "approved" ? "bg-emerald-100 text-emerald-700" : d.status === "suspended" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{d.status}</span></td>
            <td className="p-3"><div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => setStatus(d.id, "approved")}>Approve</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setStatus(d.id, "suspended")}>Suspend</Button>
            </div></td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Vehicles() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/vehicles").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.slice(0, 60).map((v) => (
        <div key={v.id} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
          <img src={v.image} alt="" className="h-16 w-20 rounded-lg object-cover" />
          <div><p className="font-medium text-navy">{v.make} {v.model}</p><p className="text-xs text-slate-500">{v.vehicle_label} · {v.reg}</p><p className="text-xs text-slate-400">{v.operator_name}</p></div>
        </div>
      ))}
    </div>
  );
}

function Applications() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/driver-applications").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const upd = async (id, status) => { await api.patch(`/admin/driver-applications/${id}`, { status }); toast.success("Application updated"); load(); };
  if (!rows) return <Loading />;
  return (
    <div className="grid gap-3">
      {rows.length === 0 ? <p className="text-sm text-slate-500">No applications yet.</p> : rows.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><span className="font-semibold text-navy">{a.application_id}</span> · <span className="text-sm">{a.personal?.name}</span><p className="text-xs text-slate-500">{a.business?.operator_name} · {a.vehicle?.make} {a.vehicle?.model} · {(a.services || []).join(", ")}</p></div>
            <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">{a.status.replace("_", " ")}</span>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => upd(a.id, "approved")} data-testid={`approve-app-${a.id}`}>Approve</Button>
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => upd(a.id, "rejected")}>Reject</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Leads() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/leads").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const upd = async (id, body) => { await api.patch(`/admin/leads/${id}`, body); toast.success("Request updated"); load(); };
  if (!rows) return <Loading />;
  return (
    <div className="grid gap-3">
      {rows.length === 0 ? <p className="text-sm text-slate-500">No taxi requests yet.</p> : rows.map((l) => (
        <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><span className="font-semibold text-navy">{l.id}</span> · {l.name} · {l.phone}<p className="text-xs text-slate-500">{l.pickup?.name} → {l.dropoff?.name || "—"} · {l.date} {l.time} · {l.passengers} pax</p>{l.message && <p className="text-xs italic text-slate-500">"{l.message}"</p>}</div>
            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">{l.status}</span>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => upd(l.id, { status: "contacted" })}>Mark Contacted</Button>
            <Button size="sm" className="bg-brand hover:bg-brand/90" onClick={() => upd(l.id, { status: "converted" })}>Converted</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Corporate() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/corporate-enquiries").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="grid gap-3">
      {rows.length === 0 ? <p className="text-sm text-slate-500">No corporate enquiries yet.</p> : rows.map((c) => (
        <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="font-semibold text-navy">{c.company}</p>
          <p className="text-sm text-slate-500">{c.contact} · {c.email} · {c.phone}</p>
          <p className="text-xs text-slate-400">Size: {c.size || "—"} · Monthly trips: {c.monthly_trips || "—"}</p>
        </div>
      ))}
    </div>
  );
}

function Pricing() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then(({ data }) => setP(data)); }, []);
  if (!p) return <Loading />;
  const save = async () => { await api.put("/admin/pricing", p); toast.success("Pricing updated — new fares apply immediately"); };
  const Field = ({ k, label }) => <div><label className="text-xs text-slate-500">{label}</label><Input type="number" step="0.01" value={p[k]} onChange={(e) => setP({ ...p, [k]: parseFloat(e.target.value) })} data-testid={`price-${k}`} /></div>;
  return (
    <Section>
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h3 className="mb-4 font-heading font-semibold text-navy">Pricing engine</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field k="base_fare" label="Base fare (£)" /><Field k="per_mile" label="Per mile (£)" /><Field k="per_minute" label="Per minute (£)" />
          <Field k="minimum_fare" label="Minimum fare (£)" /><Field k="airport_fee" label="Airport fee (£)" /><Field k="station_fee" label="Station fee (£)" />
          <Field k="night_surcharge_pct" label="Night surcharge (0-1)" />
        </div>
        <Button className="mt-5 bg-brand hover:bg-brand/90" onClick={save} data-testid="save-pricing">Save Pricing Rules</Button>
      </div>
    </Section>
  );
}

function Payments() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/payments").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Booking</th><th className="p-3">Customer</th><th className="p-3">Amount</th><th className="p-3">Status</th></tr></thead>
        <tbody>{rows.length === 0 ? <tr><td className="p-4 text-slate-500" colSpan={4}>No payments recorded yet.</td></tr> : rows.map((t) => (
          <tr key={t.session_id} className="border-b border-slate-100"><td className="p-3 font-medium text-navy">{t.booking_ref}</td><td className="p-3">{t.customer_name}</td><td className="p-3">{gbp(t.amount)}</td><td className="p-3"><span className={`rounded-full px-2 py-1 text-xs font-semibold ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{t.payment_status}</span></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Promotions() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/promotions").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const [form, setForm] = useState({ code: "", discount_type: "percent", discount_value: 10, min_fare: 20, max_discount: 15, service: "any" });
  const create = async () => { await api.post("/admin/promotions", { ...form, start_date: "2026-01-01", end_date: "2026-12-31", location: "any", usage_limit: 1000, customer_limit: 1 }); toast.success("Promo created"); load(); };
  if (!rows) return <Loading />;
  return (
    <Section>
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-3 font-heading font-semibold text-navy">Create promo code</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Input placeholder="CODE" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code" />
          <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="fixed">Fixed £</SelectItem></SelectContent></Select>
          <Input type="number" placeholder="Value" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: parseFloat(e.target.value) })} data-testid="promo-value" />
        </div>
        <Button className="mt-3 bg-brand hover:bg-brand/90" onClick={create} data-testid="create-promo">Create Promo</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between"><span className="font-heading font-bold text-navy">{p.code}</span><span className="rounded-full bg-brand-light px-2 py-0.5 text-xs font-semibold text-brand">{p.discount_type === "percent" ? `${p.discount_value}%` : gbp(p.discount_value)}</span></div>
            <p className="mt-1 text-xs text-slate-500">Min fare {gbp(p.min_fare)} · Used {p.used} · {p.service}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

function Reviews() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/reviews").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const mod = async (id, approved) => { await api.patch(`/admin/reviews/${id}`, { approved }); toast.success("Review updated"); load(); };
  if (!rows) return <Loading />;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between"><span className="font-semibold text-navy">{r.customer_name}</span><span className="text-gold">{"★".repeat(r.rating)}</span></div>
          <p className="mt-1 text-sm text-slate-600">"{r.text}"</p>
          <p className="mt-1 text-xs text-slate-400">Driver: {r.driver_name}</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant={r.approved ? "outline" : "default"} className={r.approved ? "" : "bg-emerald-600"} onClick={() => mod(r.id, true)}>Approve</Button>
            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => mod(r.id, false)}>Hide</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
